import debug from "debug";

const log = debug("app:makeparts");

/**
 * Handles automatic deletion of 3D objects from the scene based on their layer.
 * @category Experience
 * @subcategory ObjectManagement
 */

class DeleteAuto {
  /**
   * Constructs a DeleteAuto instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.scene = this.experience.scene;

    this.eventListeners();
  }

  cleanup() {
    console.log("Cleaning up DeleteAuto...");

    // Remove event listeners
    this.eventHub.off("deleteAuto", this.deleteByLayer);

    // Clear stored references
    this.scene = null;
    this.eventHub = null;
    this.experience = null;

    console.log("DeleteAuto cleanup complete.");
}

  /**
   * Sets up event listeners for delete operations.
   */
  eventListeners() {
    this.eventHub.on("deleteAuto", this.deleteByLayer.bind(this));
  }
  /**
   * Deletes all objects in the scene that belong to a specified layer.
   * @param {number} layer - The layer identifier of the objects to be deleted.
   */
  deleteByLayer(layer) {
    log("delete auto initiated");
    const objectsToRemove = [];

    // Collect all objects to remove
    this.scene.traverse((object) => {
      if (object.isGroup && object.userData.layer === layer) {
        objectsToRemove.push(object);
      }
    });

    log("objects to remove", objectsToRemove);

    const objectsForUndo = this.getObjectProperties(objectsToRemove);

    // Deeply remove and dispose of resources
    objectsToRemove.forEach((group) => {
      group.traverse((child) => {
        if (child.isMesh) {
          // Dispose of geometry
          if (child.geometry) {
            child.geometry.dispose();
          }

          // Dispose of material(s)
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }

          // Remove from scene
          this.scene.remove(child);
        }
      });

      // Finally, remove the group itself
      this.scene.remove(group);
    });

    this.eventHub.emit("logEvent", {
      eventType: "delete",
      data: { figures: objectsForUndo },
    });

    log(`Deleted all objects in groups with userData.layer = ${layer}`);
    this.eventHub.emit("xmd");
  }

  /**
   * Collects properties of objects for undo operations.
   * @param {Array} objects - The objects to extract properties from.
   * @returns {Array} An array of object properties, including position and rotation.
   */
  getObjectProperties(objects) {
    const objectsState = [];
    objects.forEach((group) => {
      // Check if the group name is "XMO"
      if (group.name === "XMO" && group.userData && group.userData.partData) {
        const state = {
          partData: group.userData.partData, // Full partData from the group
          x: group.position.x,
          z: group.position.z,
          angle: this.getRotationAngleInDegrees(group),
        };
        objectsState.push(state);
      }
    });
    return objectsState;
  }

  /**
   * Retrieves the rotation angle of an object in degrees.
   * @param {THREE.Object3D} object - The object whose rotation is being calculated.
   * @returns {number} The rotation angle in degrees.
   */
  getRotationAngleInDegrees(object) {
    const euler = new THREE.Euler().setFromQuaternion(object.quaternion, "YXZ");
    return Math.round(THREE.MathUtils.radToDeg(euler.y));
  }
}

export default DeleteAuto;
